import React, {createContext, ReactNode, useContext, useState} from 'react';
import {
    ContentType,
    EditMode,
    EditModeOption,
    EDITOR_CONTENT_TYPE,
    EditWindowMode,
    EditWindowModeOption, ModeStatus,
    WindowLandmark,
} from "../../types/editorTypes";
import {useSelectedContent} from "../../contexts/SelectedContentContext";
import {ClipOverlay, Overlay, OverlayType} from "../../reactvideoeditor/pro/types";
import {remoteAdjustWindow} from "../../queries/editQueries";
import {getOverlaysByRow} from "../../utils/editor/editorUtils";
import {MAIN_TRACK_ID} from "../../constants";
import {tempKeyMoment} from "../../types/keyMomentTypes";
import useAuth from "../../hooks/useAuth";
import {adminUser} from "../../constants/userConstants";

interface EditorContextType {
    windowLandmarks: WindowLandmark[];
    addWindowLandmark: (windowLandmark: WindowLandmark) => void;
    removeWindowLandmark: (index: number) => void;
    windowEditMode: EditWindowModeOption;
    changeWindowEditMode: (mode: EditWindowModeOption) => void;
    editorMode: EditModeOption;
    setEditorMode: (mode: EditModeOption) => void;
    setWindowLandmarks: (windowLandmarks: WindowLandmark[]) => void;
    updatingWindow?: boolean;
    setUpdatingWindow?: (updatingWindow: boolean) => void;
    adjustWindow: (contentType: ContentType | null, overlays: Overlay[], windowMode: EditWindowMode, windowLandmarks: WindowLandmark[]) => any
    editorContentType: ContentType
    setEditorContentType: (contentType: ContentType) => void
    emptyWindowLandmarks: () => void
    streamInfo: StreamInfo | null
    setStreamInfo: (info: StreamInfo | null) => void
    localKeyMoment: tempKeyMoment | null,
    setLocalKeyMoment: (localKeyMoment: tempKeyMoment | null) => void
    backupKeyMomentSelection: tempKeyMoment[]
    setBackupKeyMomentSelection: (keyMoments: tempKeyMoment[]) => void
    backupMode : ModeStatus | null,
    setBackupMode: (mode: ModeStatus | null) => void
    lCutEditMode: boolean
    setLCutEditMode: (lCutEditMode: boolean) => void
    showTrimLabels: boolean
    setShowTrimLabels: (showTrimLabels: boolean) => void
    hasEditingChanges: boolean
    setHasEditingChanges: (hasEditingChanges: boolean) => void
}

interface StreamInfo {
    url: string;
    start: number;
    end: number;
}

const defaultWindowEditMode: EditWindowModeOption = {id: EditWindowMode.auto, label: 'Auto', disabled: false}
const defaultEditMode: EditModeOption = {id: EditMode.editor, label: 'Preview', disabled: false}


const EditorContext = createContext<EditorContextType | undefined>(undefined);

export const EditorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { updateKeyMomentsWindow } = useSelectedContent()

    const [windowLandmarks, setWindowLandmarks] = useState<WindowLandmark[]>([])
    const [windowEditMode, setWindowEditMode] = useState<EditWindowModeOption>(defaultWindowEditMode)
    const [editorMode, setEditorMode] = useState<EditModeOption>(defaultEditMode)
    const [updatingWindow, setUpdatingWindow] = useState<boolean>(false)
    const [editorContentType, setEditorContentType] = useState<ContentType>(EDITOR_CONTENT_TYPE.KEY_MOMENT)
    const [streamInfo, setStreamInfo] = useState<StreamInfo | null>(null)
    const [localKeyMoment, setLocalKeyMoment] = useState<tempKeyMoment | null>(null)
    const [backupKeyMomentSelection, setBackupKeyMomentSelection] = useState<tempKeyMoment[]>([])
    const [backupMode, setBackupMode] = useState<ModeStatus | null>(null)
    const [lCutEditMode, setLCutEditMode] = useState<boolean>(false)
    const [showTrimLabels, setShowTrimLabels] = useState<boolean>(false)
    const [hasEditingChanges, setHasEditingChanges] = useState<boolean>(false)
    const {user} = useAuth()

    const addWindowLandmark = (windowLandmark: WindowLandmark) =>{
        const tempWindowLandmarks = [...windowLandmarks]
        const targetLandmark = tempWindowLandmarks.find(wld => wld.frame === windowLandmark.frame)
        if (!targetLandmark) {
            windowLandmark.isHidden = adminUser === user.email
            setWindowLandmarks([...tempWindowLandmarks, windowLandmark ])
        }else{
            targetLandmark.coordinate = windowLandmark.coordinate
        }
        setHasEditingChanges(true)
    }

    const removeWindowLandmark = (index: number) =>{
        setWindowLandmarks((prev) => prev.filter((_, i) => i !== index));
        setHasEditingChanges(true)
    }

    const emptyWindowLandmarks = ()=> {
        setWindowLandmarks([])
    }

    const changeWindowEditMode = (editMode: EditWindowModeOption) => {
        setWindowEditMode(editMode);
    }

    const adjustWindow = async (
        contentType: ContentType | null,
        overlays: ClipOverlay[],
        windowMode: EditWindowMode,
        windowLandmarks: WindowLandmark[]) => {

        try {
            setUpdatingWindow(true)
            //Nel caso in cui sto lavorando coi key moment ho una solo traccia ed è inutile filtrare per riga
            const validOverlays = (contentType === EDITOR_CONTENT_TYPE.CLIP ? getOverlaysByRow(overlays, MAIN_TRACK_ID) : overlays).filter((overlay: Overlay) => overlay.type === OverlayType.VIDEO && !overlay.subtype )
            const adjustResponse = await remoteAdjustWindow(contentType, validOverlays, windowMode, windowLandmarks)
            updateKeyMomentsWindow(adjustResponse)
            setUpdatingWindow(false)
        }catch (e){
            throw e
        }
        finally {
            setUpdatingWindow(false)
        }
    }

    return (
        <EditorContext.Provider value={{
            windowLandmarks,
            addWindowLandmark,
            removeWindowLandmark,
            windowEditMode,
            changeWindowEditMode,
            setEditorMode,
            editorMode,
            setWindowLandmarks,
            updatingWindow,
            setUpdatingWindow,
            adjustWindow,
            editorContentType,
            setEditorContentType,
            emptyWindowLandmarks,
            setStreamInfo,
            streamInfo,
            localKeyMoment,
            setLocalKeyMoment,
            backupKeyMomentSelection,
            setBackupKeyMomentSelection,
            backupMode,
            setBackupMode,
            lCutEditMode,
            setLCutEditMode,
            showTrimLabels,
            setShowTrimLabels,
            hasEditingChanges,
            setHasEditingChanges
        }}>
            {children}
        </EditorContext.Provider>
    );
};

export const useEditState = (): EditorContextType => {
    const context = useContext(EditorContext);
    if (!context) {
        throw new Error('useEditState must be in an EditorProvider');
    }
    return context;
};